// MF-67 — The one badge used across Journal for tenant colours (severities, types, categories).
// 'soft' (default) = tint + readable text + colour dot; 'solid' = full fill with auto-contrast text.
import { badgeTokens, readableText, TYPE_FALLBACK_COLORS, STATUS_COLORS } from './colorTokens'
import { useTypes } from './useLookups'

const SIZES = {
  sm: { fontSize: 12, padding: '2px 10px', dot: 6, gap: 6 },
  md: { fontSize: 13, padding: '4px 12px', dot: 8, gap: 8 },
  lg: { fontSize: 15, padding: '8px 18px', dot: 10, gap: 10 }
}

const ColorBadge = ({ label, color, size = 'md', variant = 'soft', dot = true, className = '', style }) => {
  const s = SIZES[size] || SIZES.md
  const soft = variant !== 'solid'
  const t = badgeTokens(color)
  return (
    <span
      className={`items-center ${className}`}
      style={{
        display: 'inline-flex',
        gap: s.gap,
        padding: s.padding,
        fontSize: s.fontSize,
        fontWeight: 500,
        lineHeight: 1.4,
        borderRadius: 999,
        whiteSpace: 'nowrap',
        backgroundColor: soft ? t.bg : t.dot,
        color: soft ? t.text : readableText(color),
        border: `1px solid ${soft ? t.border : t.dot}`,
        ...style
      }}
    >
      {dot && soft ? (
        <span
          aria-hidden
          style={{ width: s.dot, height: s.dot, borderRadius: '50%', backgroundColor: t.dot, flex: 'none' }}
        />
      ) : null}
      {label}
    </span>
  )
}

const cap = (s) =>
  String(s || '')
    .charAt(0)
    .toUpperCase() + String(s || '').slice(1)

// A type code rendered with the tenant's own label + colour (fallbacks for the default codes).
export const TypeBadge = ({ code = 'neutral', size = 'sm' }) => {
  const { byCode } = useTypes()
  const s = byCode.get(code)
  return (
    <ColorBadge
      size={size}
      color={s?.color || TYPE_FALLBACK_COLORS[code] || TYPE_FALLBACK_COLORS.neutral}
      label={s?.label || cap(code)}
    />
  )
}

export const StatusBadge = ({ status = 'open', size = 'sm' }) => (
  <ColorBadge
    size={size}
    color={STATUS_COLORS[status] || STATUS_COLORS.open}
    label={String(status).charAt(0).toUpperCase() + String(status).slice(1)}
  />
)

// Signed points, coloured by sign (green gain / red loss / muted zero).
export const Points = ({ value = 0 }) => {
  const n = Number(value) || 0
  const color = n > 0 ? 'rgb(21, 128, 61)' : n < 0 ? 'rgb(185, 28, 28)' : 'rgb(100, 116, 139)'
  return (
    <span style={{ color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{n > 0 ? `+${n}` : n}</span>
  )
}

// Stable technical codes (analytics keys) — quiet monospace tag.
export const CodeTag = ({ code }) =>
  code ? (
    <span
      style={{
        display: 'inline-block',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 11,
        padding: '1px 6px',
        borderRadius: 4,
        color: 'rgb(71, 85, 105)',
        backgroundColor: 'rgb(241, 245, 249)',
        border: '1px solid rgb(226, 232, 240)',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle'
      }}
    >
      {code}
    </span>
  ) : null

export default ColorBadge
